import { Injectable } from '@angular/core';
import { Meta as MetaService } from '@angular/platform-browser';
import { TimeoutService } from '@df/ng/timeout.service';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';

if (typeof window !== 'undefined') {
    window['prerenderReady'] = false;
}

@Injectable({
    providedIn: 'root'
})
export class PrerenderService {
    /**
     * Number of promises we are awaiting
     */
    private waitingQty = 0;

    /**
     * If the service is active
     * it is used for legacy prerender and prerender-mode of universal
     * it is not used for SSR mode or universal
     */
    private isOperating = false;

    constructor(
        private client: Client,
        afterContentInitDirectiveService: AfterContentInitDirectiveService,
        private app: App,
        private metaService: MetaService,
        private timeoutService: TimeoutService
    ) {
        this.isOperating = ['prerender', 'legacyPrerender'].includes(this.client.mode);
        if (this.isOperating) {
            this.wait(
                new Promise<void>(resolve => {
                    const subscription = afterContentInitDirectiveService.loaded.subscribe(value => {
                        if (value) {
                            this.timeoutService.setTimeout(() => {
                                resolve();
                            }, 3000);
                            subscription.unsubscribe();
                        }
                    });
                })
            );
        }
    }

    private ready() {
        this.recordUsedModels();
        window['prerenderReady'] = true;
    }

    /**
     * Adds promise to waiting list
     */
    wait(promise: Promise<any>, timeout: number = 0) {
        if (window['prerenderReady'] || !this.isOperating) {
            return;
        }

        this.waitingQty++;

        let expired = false;

        const onFinish = () => {
            this.timeoutService.setTimeout(() => {
                if (!expired) {
                    expired = true;
                    this.waitingQty--;
                    if (this.waitingQty < 1) {
                        this.ready();
                    }
                }
            }, 200); // lets wait a bit more
        };
        if (timeout) {
            this.timeoutService.setTimeout(onFinish, timeout);
        }
        promise.finally(onFinish);
    }

    private recordUsedModels() {
        if (!this.isOperating) {
            return;
        }
        const tags: { [id: string]: (string | number)[] } = {};
        ['block', 'page'].forEach(type => {
            let models = this.app.resources[type]?.getAllExisting();
            if (models[0] && Object.prototype.hasOwnProperty.call(models[0], 'canFlushPrerender')) {
                models = models.filter((model: any) => model.canFlushPrerender());
            }
            const ids = models.map(i => i.id);
            if (ids?.length) {
                tags[type] = ids;
            }
        });
        this.metaService.updateTag({ name: 'cache-tags', content: btoa(JSON.stringify(tags)) });
    }
}
